/**
 * Enrobage du client généré.
 *
 * Écrit à la main, parce que trois choses ne se génèrent pas depuis un contrat :
 *
 * 1. poser l'en-tête `Idempotency-Key` là où l'API l'exige, et seulement là ;
 * 2. transformer un document RFC 7807 en erreur exploitable (`ProblemError`) ;
 * 3. séparer un refus métier d'une panne réseau — l'un se raconte à
 *    l'utilisateur, l'autre se réessaye.
 *
 * Les écrans n'appellent que les fonctions de `api` ci-dessous. Chacune rend la
 * charge utile directement et **lève** en cas d'échec : pas de couple
 * `{ data, error }` à déballer dans cinq écrans différents.
 */
import createClient from "openapi-fetch";
import type { paths } from "./generated/schema";
import { API_BASE_URL } from "./config";
import { AbortedError, NetworkError, ProblemError } from "./errors";
import type {
  AcceptInvitationRequest,
  SalesRepRequest,
  AssignRequest,
  AssignmentRateRequest,
  Assignment,
  TeamResponse,
  StaffingResponse,
  ChangeRoleRequest,
  CompaniesResponse,
  Company,
  CompleteMissionRequest,
  ConsumeMissionRequest,
  CreateCompanyRequest,
  CreateCompanyResponse,
  CreditPackRequest,
  ForgotPasswordRequest,
  Grid,
  Health,
  History,
  Int64,
  InvitationLookupRequest,
  InvitationPreview,
  InviteConsultantRequest,
  InviteRequest,
  Invited,
  LoginRequest,
  Me,
  Mission,
  MissionsForConsultantResponse,
  OpenTimesheetRequest,
  OrderMissionRequest,
  PacksResponse,
  Problem,
  Receipt,
  RecipientsResponse,
  RejectTimesheetRequest,
  ResetPasswordRequest,
  SaveEntriesRequest,
  SetRecipientsRequest,
  Timesheet,
  TimesheetStatus,
  TimesheetsResponse,
  UpdateBrandingRequest,
  CompanySettingsRequest,
  BillingAccount,
  UpdateBillingRequest,
  CreditLimitRequest,
  BillingSetup,
  TopUpRequest,
  ChargesResponse,
  PublishGridRequest,
  Branding,
  UpdatePlatformBrandingRequest,
  User,
  UsersResponse,
  ConnectionHistory,
  ConnectionsResponse,
  Uuid,
  WalletConsumption,
  WalletMissions,
  WalletState,
  AcceptSignupResponse,
  RejectSignupRequest,
  Signup,
  SignupRequestBody,
  SignupStatus,
  SignupsResponse,
} from "./types";

const http = createClient<paths>({
  baseUrl: API_BASE_URL,
  // Même origine en développement (proxy Vite) comme en production (front
  // servi par le binaire) : "same-origin" est déjà le défaut de fetch, on
  // l'écrit pour que personne ne le prenne pour un oubli. Le cookie de session
  // voyage ainsi sans que le front n'ait rien à poser. Un déploiement
  // cross-origin exigerait "include" ET un CORS crédité côté back : non prévu.
  credentials: "same-origin",
});

/**
 * Chemins du contrat servis sans session. Un 401 reçu sur l'un d'eux est une
 * réponse métier (mauvais mot de passe, lien mort), pas une session tombée :
 * le middleware ci-dessous ne doit pas le prendre pour une déconnexion.
 *
 * `/me` y figure aussi : c'est l'appel qui DÉCOUVRE si l'on est connecté, et
 * son 401 signifie « anonyme », que le fournisseur de session traite lui-même.
 * Sans cette exception, la première visite d'un anonyme déclencherait le
 * gestionnaire de session perdue, qui redirigerait vers la connexion, qui
 * appellerait `/me`… la boucle classique.
 *
 * Les clés sont les chemins du contrat AVEC leurs accolades, tels que
 * openapi-fetch les rend dans `schemaPath` : indépendants des valeurs.
 */
const CHEMINS_PUBLICS: ReadonlySet<string> = new Set([
  "/api/v1/auth/login",
  "/api/v1/auth/logout",
  "/api/v1/me",
  "/api/v1/auth/password/forgot",
  "/api/v1/auth/password/reset",
  "/api/v1/invitations/lookup",
  "/api/v1/invitations/acceptance",
  "/api/v1/signups",
  "/healthz",
]);

type OnUnauthenticated = () => void;

// Point d'accroche de module et non contexte React : le client vit hors de
// React, et c'est le fournisseur de session qui s'enregistre ici au montage.
// Ce n'est pas une globale mutable au sens du dépôt (aucune donnée métier n'y
// transite) : un simple rappel, posé une fois, retiré au démontage.
let surSessionPerdue: OnUnauthenticated | null = null;

/**
 * Enregistre ce qu'il faut faire quand une route protégée répond 401 : la
 * session a expiré ou a été révoquée, et l'application doit repasser en
 * anonyme. Appelé par `SessionProvider` ; `null` pour se désinscrire.
 */
export function setUnauthenticatedHandler(fn: OnUnauthenticated | null): void {
  surSessionPerdue = fn;
}

http.use({
  onResponse({ response, schemaPath }) {
    // Le corps n'est pas lu ici : il serait consommé avant `appel()`. Sur une
    // route protégée, le back n'émet qu'un seul 401 (`unauthenticated`), le
    // statut suffit.
    if (response.status === 401 && !CHEMINS_PUBLICS.has(schemaPath)) {
      surSessionPerdue?.();
    }
    return undefined;
  },
});

/** Options communes à tout appel. */
export type RequestOptions = {
  /**
   * Signal d'annulation. À passer systématiquement depuis `useApiQuery`, qui
   * annule la requête au démontage : sans lui, une réponse arrivée après une
   * navigation écrit dans un composant qui n'existe plus.
   */
  signal?: AbortSignal;
};

/**
 * Options d'un appel idempotent.
 *
 * `idempotencyKey` porte l'identité de **l'intention**, pas celle de la
 * tentative : elle doit rester la même quand on rejoue le même fait, et
 * différer entre deux faits distincts. Concrètement, un écran la fabrique une
 * fois (`useIdempotencyKey`) et la réutilise tant que l'utilisateur n'a pas
 * changé sa saisie — en fabriquer une neuve à chaque clic reviendrait à
 * désactiver l'idempotence, et à créditer deux fois un même paiement.
 */
export type IdempotentOptions = RequestOptions & {
  idempotencyKey: string;
};

/** Forme minimale d'un retour d'openapi-fetch, suffisante pour le déballage. */
type Enveloppe<T> = {
  data?: T;
  error?: unknown;
  response: Response;
};

/** Vrai si la valeur ressemble à un document RFC 7807 de ce contrat. */
function estProblem(v: unknown): v is Problem {
  if (typeof v !== "object" || v === null) {
    return false;
  }
  const p = v as Partial<Problem>;
  return typeof p.code === "string" && typeof p.status === "number";
}

/**
 * Déballe un appel : rend la charge utile, ou lève.
 *
 * Toute erreur sort d'ici sous l'une des trois formes connues — `ProblemError`,
 * `AbortedError`, `NetworkError` — pour qu'un `catch` d'écran n'ait jamais à
 * inspecter un objet d'origine inconnue.
 */
async function appel<T>(exec: () => Promise<Enveloppe<T>>): Promise<T> {
  let enveloppe: Enveloppe<T>;
  try {
    enveloppe = await exec();
  } catch (cause) {
    // `fetch` ne rejette que sur une panne de transport ou une annulation :
    // un 500 arrive ici comme une réponse, pas comme une exception.
    if (cause instanceof DOMException && cause.name === "AbortError") {
      throw new AbortedError(cause);
    }
    throw new NetworkError("Le service est injoignable.", cause);
  }

  if (enveloppe.error !== undefined) {
    if (estProblem(enveloppe.error)) {
      throw new ProblemError(enveloppe.error, {
        retryAfter: lireRetryAfter(enveloppe.response),
      });
    }
    // Le serveur a refusé sans document lisible — un intermédiaire, un 502 de
    // reverse proxy. On fabrique un Problem plutôt que de laisser filtrer une
    // chaîne brute : les écrans ne testent qu'un `code`.
    throw new ProblemError({
      type: "urn:lde:problem:internal_error",
      title: "Réponse illisible",
      status: enveloppe.response.status,
      detail: `Le service a répondu ${enveloppe.response.status} sans diagnostic exploitable.`,
      code: "internal_error",
    });
  }

  if (enveloppe.data === undefined) {
    throw new NetworkError("Réponse vide alors qu'un corps était attendu.");
  }
  return enveloppe.data;
}

/**
 * Déballe un appel dont le succès n'a pas de corps (204, 202). openapi-fetch
 * rend alors `data: {}` ; on ne garde que le fait que ça a marché.
 */
async function appelSansCorps(exec: () => Promise<Enveloppe<unknown>>): Promise<void> {
  let enveloppe: Enveloppe<unknown>;
  try {
    enveloppe = await exec();
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") {
      throw new AbortedError(cause);
    }
    throw new NetworkError("Le service est injoignable.", cause);
  }
  if (enveloppe.error !== undefined) {
    if (estProblem(enveloppe.error)) {
      throw new ProblemError(enveloppe.error, {
        retryAfter: lireRetryAfter(enveloppe.response),
      });
    }
    throw new ProblemError({
      type: "urn:lde:problem:internal_error",
      title: "Réponse illisible",
      status: enveloppe.response.status,
      detail: `Le service a répondu ${enveloppe.response.status} sans diagnostic exploitable.`,
      code: "internal_error",
    });
  }
}

/** Lit `Retry-After` (en secondes) d'une réponse, ou `undefined`. */
function lireRetryAfter(response: Response): number | undefined {
  const brut = response.headers.get("Retry-After");
  if (brut === null) {
    return undefined;
  }
  const secondes = Number.parseInt(brut, 10);
  return Number.isFinite(secondes) && secondes > 0 ? secondes : undefined;
}

/** Paramètres de pagination de l'historique du grand livre. */
export type HistoryQuery = {
  /**
   * Curseur : reprendre tel quel le `next_before_id` de la page précédente.
   * Absent, on lit la première page (la plus récente). C'est une chaîne
   * décimale, jamais un nombre.
   */
  before_id?: Int64;
  /** Taille de page. Absent, 50 ; le serveur plafonne à 200. */
  limit?: number;
};

/** Filtres d'une liste de feuilles de temps. Tous facultatifs. */
/** Paramètres de `getWalletConsumption`. */
export type ConsumptionQuery = {
  /** Profondeur en mois, mois courant compris. Absent : 13. Plafonné à 36 par le serveur. */
  months?: number;
};

export type TimesheetsQuery = {
  status?: TimesheetStatus;
  /** ESN seulement : ne garder que les missions de cette entreprise. */
  company_id?: Uuid;
};

/** Filtre de la file des demandes d'inscription ; sans statut, toutes. */
export type SignupsQuery = {
  status?: SignupStatus;
};

export const api = {
  /** Catalogue des packs vendus. Écran « achat ». */
  listPacks(opts: RequestOptions = {}): Promise<PacksResponse> {
    return appel(() => http.GET("/api/v1/packs", { signal: opts.signal }));
  },

  /** Grille tarifaire en vigueur et ses expertises. Écrans « catalogue » et « commande ». */
  getCurrentGrid(opts: RequestOptions = {}): Promise<Grid> {
    return appel(() => http.GET("/api/v1/grid", { signal: opts.signal }));
  },

  /**
   * Publie la version suivante de la grille, complète. Réservé à
   * l'administrateur ESN ; `409 grid_conflict` si une autre publication vient
   * de passer.
   */
  publishGrid(body: PublishGridRequest, opts: RequestOptions = {}): Promise<Grid> {
    return appel(() => http.POST("/api/v1/esn/grid", { body, signal: opts.signal }));
  },

  /**
   * État d'un portefeuille : les quatre seaux, le total entré, et l'anomalie
   * comptable éventuelle. Écran « dashboard ».
   *
   * `anomaly` non nul n'est **pas** une erreur : les chiffres restent ceux du
   * grand livre et doivent être affichés, avec un avertissement visible.
   */
  getWalletState(walletID: Uuid, opts: RequestOptions = {}): Promise<WalletState> {
    return appel(() =>
      http.GET("/api/v1/wallets/{walletID}", {
        params: { path: { walletID } },
        signal: opts.signal,
      }),
    );
  },

  /**
   * Une page d'historique du grand livre, de la plus récente à la plus
   * ancienne. Écran « historique ».
   *
   * Pagination par curseur : repasser `next_before_id` dans `before_id`. Une
   * page pleine ne prouve pas qu'il reste quelque chose — seul
   * `next_before_id === null` le prouve.
   */
  getWalletHistory(
    walletID: Uuid,
    query: HistoryQuery = {},
    opts: RequestOptions = {},
  ): Promise<History> {
    return appel(() =>
      http.GET("/api/v1/wallets/{walletID}/entries", {
        params: { path: { walletID }, query },
        signal: opts.signal,
      }),
    );
  },

  /**
   * Consommation mensuelle d'un portefeuille. Écran « dashboard », histogramme
   * de consommation et projection.
   *
   * Le serveur rend des faits : ce qui a été imputé chaque mois, et le mois
   * courant `to` qui cale l'axe. La projection est calculée côté écran.
   */
  getWalletConsumption(
    walletID: Uuid,
    query: ConsumptionQuery = {},
    opts: RequestOptions = {},
  ): Promise<WalletConsumption> {
    return appel(() =>
      http.GET("/api/v1/wallets/{walletID}/consumption", {
        params: { path: { walletID }, query },
        signal: opts.signal,
      }),
    );
  },

  /**
   * Crédite un pack acheté. Écran « achat ».
   *
   * **Idempotent** : `idempotencyKey` porte la référence du paiement. Rejouer
   * la même clé rend le reçu du premier passage, sans créditer deux fois.
   */
  creditPack(
    walletID: Uuid,
    body: CreditPackRequest,
    opts: IdempotentOptions,
  ): Promise<Receipt> {
    return appel(() =>
      http.POST("/api/v1/wallets/{walletID}/purchases", {
        params: {
          path: { walletID },
          header: { "Idempotency-Key": opts.idempotencyKey },
        },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** Missions d'un portefeuille et leur avancement. Complète le dashboard. */
  listWalletMissions(walletID: Uuid, opts: RequestOptions = {}): Promise<WalletMissions> {
    return appel(() =>
      http.GET("/api/v1/wallets/{walletID}/missions", {
        params: { path: { walletID } },
        signal: opts.signal,
      }),
    );
  },

  /**
   * Commande une prestation : estime l'enveloppe, la réserve, crée la mission.
   * Écran « commande ».
   *
   * **Non idempotent, et c'est voulu par le contrat** : deux appels identiques
   * créent deux missions et réservent deux enveloppes. Il n'y a pas d'en-tête
   * d'idempotence ici — l'API l'ignorerait. Un écran doit donc désactiver son
   * bouton pendant l'envoi, et relire `listWalletMissions` avant de réessayer
   * un appel resté sans réponse.
   *
   * Un refus pour solde insuffisant arrive en `409 insufficient_funds` avec
   * `requested` et `available` : voir `insufficientFunds(err)`.
   */
  orderMission(
    walletID: Uuid,
    body: OrderMissionRequest,
    opts: RequestOptions = {},
  ): Promise<Mission> {
    return appel(() =>
      http.POST("/api/v1/wallets/{walletID}/missions", {
        params: { path: { walletID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /**
   * Impute du travail réel sur une mission.
   *
   * **Idempotent** : `idempotencyKey` porte la référence du fait imputé (ligne
   * de feuille de temps). C'est elle seule qui distingue deux journées de même
   * montant d'un même envoi rejoué.
   *
   * Un dépassement d'enveloppe est refusé en `409 envelope_overrun` avec
   * `requested` et `remaining` : voir `envelopeOverrun(err)`.
   */
  consumeMission(
    missionID: Uuid,
    body: ConsumeMissionRequest,
    opts: IdempotentOptions,
  ): Promise<Mission> {
    return appel(() =>
      http.POST("/api/v1/missions/{missionID}/consumptions", {
        params: {
          path: { missionID },
          header: { "Idempotency-Key": opts.idempotencyKey },
        },
        body,
        signal: opts.signal,
      }),
    );
  },

  /**
   * Clôture une mission : imputation finale s'il reste quelque chose, puis
   * libération du reliquat. `tokens: "0"` quand il n'y a plus rien à imputer.
   *
   * Une mission déjà close rend `409 invalid_transition`.
   */
  completeMission(
    missionID: Uuid,
    body: CompleteMissionRequest,
    opts: RequestOptions = {},
  ): Promise<Mission> {
    return appel(() =>
      http.POST("/api/v1/missions/{missionID}/completion", {
        params: { path: { missionID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /**
   * Annule une mission : le **reliquat** d'enveloppe repart au disponible. Sans
   * corps. Une mission déjà close rend `409 invalid_transition`.
   */
  cancelMission(missionID: Uuid, opts: RequestOptions = {}): Promise<Mission> {
    return appel(() =>
      http.POST("/api/v1/missions/{missionID}/cancellation", {
        params: { path: { missionID } },
        signal: opts.signal,
      }),
    );
  },

  /** Sonde d'exploitation. Sert au bandeau « service indisponible ». */
  health(opts: RequestOptions = {}): Promise<Health> {
    return appel<Health>(() => http.GET("/healthz", { signal: opts.signal }));
  },

  // ————————————————————————————— Session —————————————————————————————

  /**
   * Ouvre une session. Le cookie est posé par la réponse ; le corps décrit la
   * session comme `getMe`. Un refus arrive en `401 invalid_credentials`,
   * `423 account_locked` ou `429 rate_limited` (voir `retryAfter`).
   */
  login(body: LoginRequest, opts: RequestOptions = {}): Promise<Me> {
    return appel(() => http.POST("/api/v1/auth/login", { body, signal: opts.signal }));
  },

  /** Ferme la session et efface le cookie. */
  logout(opts: RequestOptions = {}): Promise<void> {
    return appelSansCorps(() => http.POST("/api/v1/auth/logout", { signal: opts.signal }));
  },

  /** La session courante : compte, entreprise, droits. `401` si anonyme. */
  getMe(opts: RequestOptions = {}): Promise<Me> {
    return appel(() => http.GET("/api/v1/me", { signal: opts.signal }));
  },

  /** Demande un lien de réinitialisation. Rend toujours succès (202). */
  forgotPassword(body: ForgotPasswordRequest, opts: RequestOptions = {}): Promise<void> {
    return appelSansCorps(() =>
      http.POST("/api/v1/auth/password/forgot", { body, signal: opts.signal }),
    );
  },

  /** Pose un nouveau mot de passe depuis un lien. `410` si le lien ne vaut plus. */
  resetPassword(body: ResetPasswordRequest, opts: RequestOptions = {}): Promise<void> {
    return appelSansCorps(() =>
      http.POST("/api/v1/auth/password/reset", { body, signal: opts.signal }),
    );
  },

  /** Décrit une invitation. `410` expirée, `409` déjà acceptée, `404` inconnue. */
  lookupInvitation(
    body: InvitationLookupRequest,
    opts: RequestOptions = {},
  ): Promise<InvitationPreview> {
    return appel(() => http.POST("/api/v1/invitations/lookup", { body, signal: opts.signal }));
  },

  /** Accepte une invitation : active le compte et ouvre la session. */
  acceptInvitation(body: AcceptInvitationRequest, opts: RequestOptions = {}): Promise<Me> {
    return appel(() =>
      http.POST("/api/v1/invitations/acceptance", { body, signal: opts.signal }),
    );
  },

  // ————————————————————————————— Entreprises —————————————————————————————

  /** Toutes les entreprises clientes. ESN seulement. */
  listCompanies(opts: RequestOptions = {}): Promise<CompaniesResponse> {
    return appel(() => http.GET("/api/v1/companies", { signal: opts.signal }));
  },

  /** Crée une entreprise, son portefeuille et, si demandé, son premier admin. */
  createCompany(body: CreateCompanyRequest, opts: RequestOptions = {}): Promise<CreateCompanyResponse> {
    return appel(() => http.POST("/api/v1/companies", { body, signal: opts.signal }));
  },

  // ————————————————————————————— Inscription —————————————————————————————

  /**
   * Dépose une demande d'inscription depuis la page publique. Rend toujours
   * succès (202) : la réponse ne dit ni si l'adresse a déjà un compte, ni si
   * une demande est déjà ouverte.
   */
  requestSignup(body: SignupRequestBody, opts: RequestOptions = {}): Promise<void> {
    return appelSansCorps(() => http.POST("/api/v1/signups", { body, signal: opts.signal }));
  },

  /** La file des demandes, pour l'ESN. */
  listSignups(query: SignupsQuery = {}, opts: RequestOptions = {}): Promise<SignupsResponse> {
    return appel(() =>
      http.GET("/api/v1/esn/signups", { params: { query: { status: query.status } }, signal: opts.signal }),
    );
  },

  /** Accepte une demande : crée l'entreprise et invite le contact. `409` si déjà tranchée ou adresse prise. */
  acceptSignup(signupID: Uuid, opts: RequestOptions = {}): Promise<AcceptSignupResponse> {
    return appel(() =>
      http.POST("/api/v1/esn/signups/{signupID}/acceptance", {
        params: { path: { signupID } },
        signal: opts.signal,
      }),
    );
  },

  /** Refuse une demande avec un motif interne. `409` si déjà tranchée. */
  rejectSignup(signupID: Uuid, body: RejectSignupRequest, opts: RequestOptions = {}): Promise<Signup> {
    return appel(() =>
      http.POST("/api/v1/esn/signups/{signupID}/rejection", {
        params: { path: { signupID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** Fiche d'une entreprise. */
  getCompany(companyID: Uuid, opts: RequestOptions = {}): Promise<Company> {
    return appel(() =>
      http.GET("/api/v1/companies/{companyID}", {
        params: { path: { companyID } },
        signal: opts.signal,
      }),
    );
  },

  /** Nom et couleur d'accent. Un champ absent est conservé. */
  updateCompanyBranding(
    companyID: Uuid,
    body: UpdateBrandingRequest,
    opts: RequestOptions = {},
  ): Promise<Company> {
    return appel(() =>
      http.PATCH("/api/v1/companies/{companyID}", {
        params: { path: { companyID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /**
   * Règle la validation des feuilles de temps de l'entreprise. Réservé à
   * l'administrateur ESN.
   */
  updateCompanySettings(
    companyID: Uuid,
    body: CompanySettingsRequest,
    opts: RequestOptions = {},
  ): Promise<Company> {
    return appel(() =>
      http.PUT("/api/v1/companies/{companyID}/settings", {
        params: { path: { companyID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /**
   * Remplace le logo. Le fichier part tel quel, avec son type déclaré ; le
   * back le vérifie par le contenu et refuse un SVG quoi qu'on lui dise.
   */
  uploadCompanyLogo(companyID: Uuid, file: File, opts: RequestOptions = {}): Promise<Company> {
    return appel(() =>
      http.PUT("/api/v1/companies/{companyID}/logo", {
        params: { path: { companyID } },
        // Le contrat type un corps binaire en `string` ; le fichier est ce
        // que fetch attend réellement. Cast localisé, et rien d'autre.
        body: file as unknown as string,
        bodySerializer: (b) => b as unknown as BodyInit,
        headers: { "Content-Type": file.type },
        signal: opts.signal,
      }),
    );
  },

  // ————————————————————————————— Marque blanche —————————————————————————————

  /** L'identité de l'instance. Publique : lue avant toute session. */
  getBranding(opts: RequestOptions = {}): Promise<Branding> {
    return appel(() => http.GET("/api/v1/branding", { signal: opts.signal }));
  },

  /** Règle l'identité, complète. Administrateur ESN. */
  updateBranding(body: UpdatePlatformBrandingRequest, opts: RequestOptions = {}): Promise<Branding> {
    return appel(() => http.PATCH("/api/v1/esn/branding", { body, signal: opts.signal }));
  },

  /** Remplace le logo de la plateforme. Même contrat que celui d'une entreprise. */
  uploadBrandingLogo(file: File, opts: RequestOptions = {}): Promise<Branding> {
    return appel(() =>
      http.PUT("/api/v1/esn/branding/logo", {
        body: file as unknown as string,
        bodySerializer: (b) => b as unknown as BodyInit,
        headers: { "Content-Type": file.type },
        signal: opts.signal,
      }),
    );
  },

  /** Retire le logo de la plateforme. */
  deleteBrandingLogo(opts: RequestOptions = {}): Promise<Branding> {
    return appel(() => http.DELETE("/api/v1/esn/branding/logo", { signal: opts.signal }));
  },

  /** Retire le logo. */
  deleteCompanyLogo(companyID: Uuid, opts: RequestOptions = {}): Promise<Company> {
    return appel(() =>
      http.DELETE("/api/v1/companies/{companyID}/logo", {
        params: { path: { companyID } },
        signal: opts.signal,
      }),
    );
  },

  // ————————————————————————————— Membres —————————————————————————————

  /** Membres d'une entreprise : actifs, invités, retirés. */
  listCompanyMembers(companyID: Uuid, opts: RequestOptions = {}): Promise<UsersResponse> {
    return appel(() =>
      http.GET("/api/v1/companies/{companyID}/members", {
        params: { path: { companyID } },
        signal: opts.signal,
      }),
    );
  },

  /** Invite un membre. `409 email_taken` si l'adresse a déjà un compte. */
  inviteCompanyMember(
    companyID: Uuid,
    body: InviteRequest,
    opts: RequestOptions = {},
  ): Promise<Invited> {
    return appel(() =>
      http.POST("/api/v1/companies/{companyID}/invitations", {
        params: { path: { companyID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** Renvoie une invitation à un membre encore invité. */
  resendInvitation(companyID: Uuid, userID: Uuid, opts: RequestOptions = {}): Promise<Invited> {
    return appel(() =>
      http.POST("/api/v1/companies/{companyID}/members/{userID}/invitation", {
        params: { path: { companyID, userID } },
        signal: opts.signal,
      }),
    );
  },

  /** Change le rôle d'un membre. `409 last_admin` / `self_target` possibles. */
  changeMemberRole(
    companyID: Uuid,
    userID: Uuid,
    body: ChangeRoleRequest,
    opts: RequestOptions = {},
  ): Promise<User> {
    return appel(() =>
      http.POST("/api/v1/companies/{companyID}/members/{userID}/role", {
        params: { path: { companyID, userID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** Retire un membre (désactivation). */
  removeMember(companyID: Uuid, userID: Uuid, opts: RequestOptions = {}): Promise<void> {
    return appelSansCorps(() =>
      http.DELETE("/api/v1/companies/{companyID}/members/{userID}", {
        params: { path: { companyID, userID } },
        signal: opts.signal,
      }),
    );
  },

  /** Comptes de l'ESN. `esn_admin` seulement. */
  listEsnUsers(opts: RequestOptions = {}): Promise<UsersResponse> {
    return appel(() => http.GET("/api/v1/esn/users", { signal: opts.signal }));
  },

  /**
   * État de connexion de tous les comptes : équipe ESN, consultants, membres
   * des entreprises. `esn_admin` seulement.
   */
  listConnections(opts: RequestOptions = {}): Promise<ConnectionsResponse> {
    return appel(() => http.GET("/api/v1/esn/connections", { signal: opts.signal }));
  },

  /**
   * Historique de connexions d'un compte, du plus récent au plus ancien, par
   * curseur comme l'historique du grand livre. `esn_admin` seulement.
   */
  listUserConnections(
    userID: Uuid,
    query: HistoryQuery = {},
    opts: RequestOptions = {},
  ): Promise<ConnectionHistory> {
    return appel(() =>
      http.GET("/api/v1/esn/users/{userID}/connections", {
        params: { path: { userID }, query },
        signal: opts.signal,
      }),
    );
  },

  /** Invite un compte ESN. */
  inviteEsnUser(body: InviteRequest, opts: RequestOptions = {}): Promise<Invited> {
    return appel(() => http.POST("/api/v1/esn/invitations", { body, signal: opts.signal }));
  },

  /** Renvoie une invitation ESN. */
  resendEsnInvitation(userID: Uuid, opts: RequestOptions = {}): Promise<Invited> {
    return appel(() =>
      http.POST("/api/v1/esn/users/{userID}/invitation", {
        params: { path: { userID } },
        signal: opts.signal,
      }),
    );
  },

  /** Change le rôle d'un compte ESN. */
  changeEsnUserRole(userID: Uuid, body: ChangeRoleRequest, opts: RequestOptions = {}): Promise<User> {
    return appel(() =>
      http.POST("/api/v1/esn/users/{userID}/role", {
        params: { path: { userID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** Retire un compte ESN. */
  removeEsnUser(userID: Uuid, opts: RequestOptions = {}): Promise<void> {
    return appelSansCorps(() =>
      http.DELETE("/api/v1/esn/users/{userID}", {
        params: { path: { userID } },
        signal: opts.signal,
      }),
    );
  },

  // ————————————————————————————— Feuilles de temps —————————————————————————————

  /** Les missions du consultant de la session. Aucun montant dedans, par type. */
  listMyMissions(opts: RequestOptions = {}): Promise<MissionsForConsultantResponse> {
    return appel(() => http.GET("/api/v1/me/missions", { signal: opts.signal }));
  },

  /** Les feuilles du consultant de la session. */
  listMyTimesheets(opts: RequestOptions = {}): Promise<TimesheetsResponse> {
    return appel(() => http.GET("/api/v1/me/timesheets", { signal: opts.signal }));
  },

  /**
   * Ouvre la feuille d'un mois : celle qui existe, sinon un brouillon neuf.
   * `403` sur une mission qui n'est pas la sienne, `422 timesheet_month_in_future`
   * sur un mois à venir.
   */
  openTimesheet(body: OpenTimesheetRequest, opts: RequestOptions = {}): Promise<Timesheet> {
    return appel(() => http.POST("/api/v1/me/timesheets", { body, signal: opts.signal }));
  },

  /** Une feuille et ses jours. Les montants n'y sont que pour qui a le droit. */
  getTimesheet(sheetID: Uuid, opts: RequestOptions = {}): Promise<Timesheet> {
    return appel(() =>
      http.GET("/api/v1/timesheets/{sheetID}", { params: { path: { sheetID } }, signal: opts.signal }),
    );
  },

  /** Remplace tous les jours. `409 timesheet_locked` sur une feuille soumise. */
  saveTimesheetEntries(sheetID: Uuid, body: SaveEntriesRequest, opts: RequestOptions = {}): Promise<Timesheet> {
    return appel(() =>
      http.PUT("/api/v1/timesheets/{sheetID}/entries", {
        params: { path: { sheetID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** Soumet à la validation. `422 timesheet_empty` sans aucun jour. */
  submitTimesheet(sheetID: Uuid, opts: RequestOptions = {}): Promise<Timesheet> {
    return appel(() =>
      http.POST("/api/v1/timesheets/{sheetID}/submission", { params: { path: { sheetID } }, signal: opts.signal }),
    );
  },

  /**
   * Approuve et impute sur la mission. `409 envelope_overrun` chiffré si
   * l'enveloppe ne suffit pas : rien n'est écrit, la feuille reste soumise.
   */
  approveTimesheet(sheetID: Uuid, opts: RequestOptions = {}): Promise<Timesheet> {
    return appel(() =>
      http.POST("/api/v1/timesheets/{sheetID}/approval", { params: { path: { sheetID } }, signal: opts.signal }),
    );
  },

  /** Refuse avec un motif ; la feuille redevient modifiable. */
  rejectTimesheet(sheetID: Uuid, body: RejectTimesheetRequest, opts: RequestOptions = {}): Promise<Timesheet> {
    return appel(() =>
      http.POST("/api/v1/timesheets/{sheetID}/rejection", {
        params: { path: { sheetID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** Les feuilles des missions d'une entreprise, pour ses valideurs et l'ESN. */
  listCompanyTimesheets(companyID: Uuid, query: TimesheetsQuery = {}, opts: RequestOptions = {}): Promise<TimesheetsResponse> {
    return appel(() =>
      http.GET("/api/v1/companies/{companyID}/timesheets", {
        params: { path: { companyID }, query: { status: query.status } },
        signal: opts.signal,
      }),
    );
  },

  /** Toutes les feuilles, pour l'ESN. */
  listEsnTimesheets(query: TimesheetsQuery = {}, opts: RequestOptions = {}): Promise<TimesheetsResponse> {
    return appel(() =>
      http.GET("/api/v1/esn/timesheets", {
        params: { query: { status: query.status, company_id: query.company_id } },
        signal: opts.signal,
      }),
    );
  },

  /** Désigne le commercial référent d'un projet (ESN) ; `null` le retire. */
  setMissionSalesRep(missionID: Uuid, body: SalesRepRequest, opts: RequestOptions = {}): Promise<Mission> {
    return appel(() =>
      http.PUT("/api/v1/missions/{missionID}/sales-rep", {
        params: { path: { missionID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** L'équipe d'un projet : affectations, tarifs, et expertises du projet. */
  getTeam(missionID: Uuid, opts: RequestOptions = {}): Promise<TeamResponse> {
    return appel(() =>
      http.GET("/api/v1/missions/{missionID}/assignments", {
        params: { path: { missionID } },
        signal: opts.signal,
      }),
    );
  },

  /** Affecte un consultant à un projet (ESN). Sans tarif, celui de l'expertise. */
  assignConsultant(missionID: Uuid, body: AssignRequest, opts: RequestOptions = {}): Promise<Assignment> {
    return appel(() =>
      http.POST("/api/v1/missions/{missionID}/assignments", {
        params: { path: { missionID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** Retarife une affectation (ESN), tant qu'aucune feuille n'est imputée. */
  setAssignmentRate(
    missionID: Uuid,
    assignmentID: Uuid,
    body: AssignmentRateRequest,
    opts: RequestOptions = {},
  ): Promise<Assignment> {
    return appel(() =>
      http.PATCH("/api/v1/missions/{missionID}/assignments/{assignmentID}", {
        params: { path: { missionID, assignmentID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** Retire un consultant d'un projet (ESN), si aucune feuille n'attend. */
  unassignConsultant(missionID: Uuid, assignmentID: Uuid, opts: RequestOptions = {}): Promise<void> {
    return appelSansCorps(() =>
      http.DELETE("/api/v1/missions/{missionID}/assignments/{assignmentID}", {
        params: { path: { missionID, assignmentID } },
        signal: opts.signal,
      }),
    );
  },

  /** Le coût de chaque consultant par projet (ESN). */
  listStaffing(opts: RequestOptions = {}): Promise<StaffingResponse> {
    return appel(() => http.GET("/api/v1/esn/staffing", { signal: opts.signal }));
  },

  /** Qui reçoit et valide les feuilles d'une entreprise. */
  getTimesheetRecipients(companyID: Uuid, opts: RequestOptions = {}): Promise<RecipientsResponse> {
    return appel(() =>
      http.GET("/api/v1/companies/{companyID}/timesheet-recipients", {
        params: { path: { companyID } },
        signal: opts.signal,
      }),
    );
  },

  /** Remplace les membres ajoutés aux destinataires. */
  setTimesheetRecipients(companyID: Uuid, body: SetRecipientsRequest, opts: RequestOptions = {}): Promise<RecipientsResponse> {
    return appel(() =>
      http.PUT("/api/v1/companies/{companyID}/timesheet-recipients", {
        params: { path: { companyID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  // ————————————————————————————— Paiement —————————————————————————————

  /** L'état du prélèvement automatique de l'entreprise. */
  getCompanyBilling(companyID: Uuid, opts: RequestOptions = {}): Promise<BillingAccount> {
    return appel(() =>
      http.GET("/api/v1/companies/{companyID}/billing", {
        params: { path: { companyID } },
        signal: opts.signal,
      }),
    );
  },

  /** Active ou coupe le pay as you go. Réservé à l'administrateur du client. */
  updateCompanyBilling(companyID: Uuid, body: UpdateBillingRequest, opts: RequestOptions = {}): Promise<BillingAccount> {
    return appel(() =>
      http.PATCH("/api/v1/companies/{companyID}/billing", {
        params: { path: { companyID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** Ouvre la signature d'un mandat et rend l'URL où envoyer le navigateur. */
  startBillingSetup(companyID: Uuid, opts: RequestOptions = {}): Promise<BillingSetup> {
    return appel(() =>
      http.POST("/api/v1/companies/{companyID}/billing/setup", {
        params: { path: { companyID } },
        signal: opts.signal,
      }),
    );
  },

  /** Fixe la limite de crédit. Réservé à l'administrateur ESN. */
  setCompanyCreditLimit(companyID: Uuid, body: CreditLimitRequest, opts: RequestOptions = {}): Promise<BillingAccount> {
    return appel(() =>
      http.PUT("/api/v1/companies/{companyID}/billing/credit-limit", {
        params: { path: { companyID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** Règle la recharge automatique. Réservé à l'administrateur du client. */
  setCompanyTopUp(companyID: Uuid, body: TopUpRequest, opts: RequestOptions = {}): Promise<BillingAccount> {
    return appel(() =>
      http.PUT("/api/v1/companies/{companyID}/billing/topup", {
        params: { path: { companyID } },
        body,
        signal: opts.signal,
      }),
    );
  },

  /** Les derniers prélèvements de l'entreprise, du plus récent au plus ancien. */
  listCompanyCharges(companyID: Uuid, opts: RequestOptions = {}): Promise<ChargesResponse> {
    return appel(() =>
      http.GET("/api/v1/companies/{companyID}/billing/charges", {
        params: { path: { companyID } },
        signal: opts.signal,
      }),
    );
  },

  // ————————————————————————————— Consultants —————————————————————————————

  /** Les consultants de l'ESN, à tout compte ESN. */
  listConsultants(opts: RequestOptions = {}): Promise<UsersResponse> {
    return appel(() => http.GET("/api/v1/esn/consultants", { signal: opts.signal }));
  },

  /** Invite un consultant : une adresse, le rôle est porté par la route. */
  inviteConsultant(body: InviteConsultantRequest, opts: RequestOptions = {}): Promise<Invited> {
    return appel(() => http.POST("/api/v1/esn/consultants/invitations", { body, signal: opts.signal }));
  },

  /** Renvoie l'invitation d'un consultant encore invité. */
  resendConsultantInvitation(userID: Uuid, opts: RequestOptions = {}): Promise<Invited> {
    return appel(() =>
      http.POST("/api/v1/esn/consultants/{userID}/invitation", { params: { path: { userID } }, signal: opts.signal }),
    );
  },

  /** Retire un consultant (désactivation). */
  removeConsultant(userID: Uuid, opts: RequestOptions = {}): Promise<void> {
    return appelSansCorps(() =>
      http.DELETE("/api/v1/esn/consultants/{userID}", { params: { path: { userID } }, signal: opts.signal }),
    );
  },
};

export type Api = typeof api;
